import { useState } from "react";
import { Fish, Sparkle, Snowflake, Anchor } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ImageCaptionField } from "@/components/ImageCaptionField";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Fresh catch off the boat — prawns, crab, bombil, levta, onjal, whatever is landing.
export const FreshCatch = ({ data, mine, busy, act, where }) => {
  const [lot, setLot] = useState({
    species: "prawns", local_name: "", sell_by: "per kg", price: 0, quantity: 10,
    state: "fresh", boat: "", harbour: "", preps: ["whole as landed"], ice_pack: true,
    cold_chain_hours: 4, note: "", image_id: "", image_caption: "",
  });
  const [order, setOrder] = useState({});
  const [landing, setLanding] = useState(null);
  const [place, setPlace] = useState("");

  const togglePrep = (p) => setLot({
    ...lot,
    preps: lot.preps.includes(p) ? lot.preps.filter((x) => x !== p) : [...lot.preps, p],
  });

  return (
    <div className="space-y-4" data-testid="catch-section">
      <div>
        <p className="overline text-slate-500">
          <Fish size={13} className="mr-1 inline align-[-2px] text-[#002FA7]" /> Fresh catch
        </p>
        {(data.catch || []).length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">Nothing landed on the board yet.</p>
        ) : (
          <div className="mt-2 grid gap-2 sm:grid-cols-3">
            {data.catch.map((c) => (
              <div key={c.id} className="border border-border bg-white p-3" data-testid={`catch-${c.id}`}>
                {c.image_url && (
                  <img src={`${BACKEND}${c.image_url}`} alt={c.species} loading="lazy"
                    className="mb-2 h-32 w-full border border-border object-cover"
                    data-testid={`catch-photo-${c.id}`} />
                )}
                <p className="text-sm font-semibold text-[#0F172A]">
                  {c.species}{c.local_name ? ` · ${c.local_name}` : ""}
                </p>
                <p className="text-xs text-slate-500">
                  {c.state}{c.boat ? ` · off the ${c.boat}` : ""}
                  {c.harbour ? ` · ${c.harbour}` : ""} · {c.stall_name}
                </p>
                <p className="mt-1 text-sm font-semibold text-[#0B5C2E]">
                  {c.symbol}{Number(c.price || 0).toFixed(2)} {c.sell_by} · {c.quantity} left
                </p>
                <p className="text-xs text-slate-500">
                  {(c.preps || []).join(" · ")}
                  {c.ice_pack ? " · ice pack on delivery" : ""}
                  {c.cold_chain_hours ? ` · out within ${c.cold_chain_hours}h of landing` : ""}
                </p>
                {c.owner_id !== data.me.id && (
                  <div className="mt-2 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <label className="flex items-center gap-1 text-xs text-slate-600">
                        How many
                        <Input data-testid={`catch-qty-${c.id}`} type="number" min={0} step="0.5" className="w-16"
                          value={order[c.id]?.quantity ?? 1}
                          onChange={(e) => setOrder({ ...order, [c.id]: { ...order[c.id], quantity: e.target.value } })} />
                      </label>
                      <label className="flex items-center gap-1 text-xs text-slate-600">
                        My price
                        <Input data-testid={`catch-offer-${c.id}`} type="number" min={0} step="0.01" className="w-20"
                          placeholder={String(c.price)} value={order[c.id]?.offer ?? ""}
                          onChange={(e) => setOrder({ ...order, [c.id]: { ...order[c.id], offer: e.target.value } })} />
                      </label>
                      <select data-testid={`catch-prep-${c.id}`} aria-label="Prepared"
                        value={order[c.id]?.prep || (c.preps || [])[0] || ""}
                        onChange={(e) => setOrder({ ...order, [c.id]: { ...order[c.id], prep: e.target.value } })}
                        className="tap-safe border border-border bg-white px-2 py-1.5 text-xs">
                        {(c.preps || []).map((p) => <option key={p} value={p}>{String(p)}</option>)}
                      </select>
                      <select data-testid={`catch-how-${c.id}`} aria-label="How"
                        value={order[c.id]?.how || "collection"}
                        onChange={(e) => setOrder({ ...order, [c.id]: { ...order[c.id], how: e.target.value } })}
                        className="tap-safe border border-border bg-white px-2 py-1.5 text-xs">
                        <option value="collection">Collect at the harbour</option>
                        <option value="delivery">Run it round to me</option>
                      </select>
                      <select data-testid={`catch-pay-${c.id}`} aria-label="Pay"
                        value={order[c.id]?.pay || "online"}
                        onChange={(e) => setOrder({ ...order, [c.id]: { ...order[c.id], pay: e.target.value } })}
                        className="tap-safe border border-border bg-white px-2 py-1.5 text-xs">
                        <option value="online">Pay online</option>
                        <option value="on_handover">Pay on handover</option>
                      </select>
                    </div>
                    {order[c.id]?.how === "delivery" && (
                      <Input data-testid={`catch-address-${c.id}`} placeholder="Where shall they drop it?"
                        value={order[c.id]?.address || ""}
                        onChange={(e) => setOrder({ ...order, [c.id]: { ...order[c.id], address: e.target.value } })} />
                    )}
                    <Button size="sm" data-testid={`catch-buy-${c.id}`} disabled={busy}
                      onClick={() => act(async () => {
                        const o = order[c.id] || {};
                        const { data: made } = await api.post("/farm/catch/orders", {
                          catch_id: c.id, quantity: Number(o.quantity) || 1,
                          prep: o.prep || (c.preps || [])[0],
                          offer_price: o.offer === "" || o.offer == null ? null : Number(o.offer),
                          how: o.how || "collection", address: o.address || "",
                          lat: where?.lat, lng: where?.lng, pay: o.pay || "online",
                        });
                        setOrder({ ...order, [c.id]: {} });
                        if ((o.pay || "online") === "online" && made.status === "ordered") {
                          const { data: p } = await api.post("/payments/farm/catch/checkout", {
                            order_id: made.id, origin_url: window.location.origin });
                          if (p.url) window.location.href = p.url;
                        }
                      }, "Ordered off the boat")}>
                      Buy it
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border border-border bg-white p-4" data-testid="catch-landing-box">
        <p className="overline text-slate-500">
          <Sparkle size={13} className="mr-1 inline align-[-2px] text-[#B3241B]" /> What is landing near me
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Input data-testid="catch-landing-where" className="max-w-xs"
            placeholder="Harbour or town — 'Navsari, Gujarat'"
            value={place} onChange={(e) => setPlace(e.target.value)} />
          <Button size="sm" variant="outline" data-testid="catch-landing-go" disabled={busy}
            onClick={async () => {
              try {
                const { data: d } = await api.post("/farm/catch/landing", {
                  where: place, lat: where?.lat, lng: where?.lng });
                setLanding(d);
                toast.success(d.line);
              } catch (e) { oops(e); }
            }}>
            <Anchor size={14} className="mr-1.5" /> Ask the harbour
          </Button>
        </div>
        {landing && (
          <div className="mt-3 border-l-2 border-[#002FA7] bg-[#EEF3FF] px-3 py-2" data-testid="catch-landing-out">
            <p className="text-sm font-semibold text-[#0F172A]">
              {landing.harbour} · {landing.country} · {landing.season}
            </p>
            <ul className="mt-1 space-y-1 text-sm text-slate-700">
              {(landing.landing || []).map((l, i) => (
                <li key={i} data-testid={`landing-${i}`}>
                  <span className="font-semibold text-[#0F172A]">{l.species}</span>
                  {l.local_name ? ` (${l.local_name})` : ""} · {landing.symbol}
                  {Number(l.price || 0).toFixed(2)} {l.sold} · {l.note}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {(data.catch_selling || []).length > 0 && (
        <div data-testid="catch-selling">
          <p className="overline text-slate-500">People buying your catch</p>
          <div className="mt-2 space-y-2">
            {data.catch_selling.map((o) => (
              <div key={o.id} className="border border-border bg-white p-3 text-sm"
                data-testid={`catch-sale-${o.id}`}>
                <p className="font-semibold text-[#0F172A]">
                  {o.reference} · {o.name} · {o.quantity} {o.sell_by} {o.species} ·
                  {" "}{o.symbol}{Number(o.total || 0).toFixed(2)} · {o.status}
                </p>
                <p className="text-xs text-slate-500">
                  {o.prep} · {o.how}{o.miles != null ? ` · ${o.miles} miles` : ""} ·
                  {" "}{o.pay === "online" ? "paying online" : "paying on handover"}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {o.status === "offered" && (
                    <>
                      <Button size="sm" data-testid={`catch-accept-${o.id}`} disabled={busy}
                        onClick={() => act(() => api.post(`/farm/catch/orders/${o.id}/respond`, { accept: true }), "Offer taken")}>
                        Take the offer
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`catch-decline-${o.id}`} disabled={busy}
                        onClick={() => act(() => api.post(`/farm/catch/orders/${o.id}/respond`, { accept: false }), "Turned down")}>
                        No thanks
                      </Button>
                    </>
                  )}
                  {o.status === "ordered" && (
                    <Button size="sm" variant="outline" data-testid={`catch-handover-${o.id}`} disabled={busy}
                      onClick={() => act(() => api.post(`/farm/catch/orders/${o.id}/handed-over`), "Handed over")}>
                      Handed over
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(data.catch_buying || []).length > 0 && (
        <div data-testid="catch-buying">
          <p className="overline text-slate-500">Fish you have bought</p>
          <ul className="mt-2 space-y-1 text-sm text-slate-700">
            {data.catch_buying.map((o) => (
              <li key={o.id} data-testid={`catch-order-${o.id}`}>
                {o.reference} · {o.quantity} {o.sell_by} {o.species} ({o.prep}) ·
                {" "}{o.symbol}{Number(o.total || 0).toFixed(2)} · {o.status} · {o.payment_status}
                {o.ice_pack && o.how === "delivery" && (
                  <Snowflake size={12} className="ml-1 inline align-[-2px] text-[#002FA7]" />
                )}
                {o.pay === "online" && o.payment_status === "unpaid" && o.status === "ordered" && (
                  <Button className="ml-2" size="sm" data-testid={`catch-pay-now-${o.id}`} disabled={busy}
                    onClick={async () => {
                      try {
                        const { data: p } = await api.post("/payments/farm/catch/checkout", {
                          order_id: o.id, origin_url: window.location.origin });
                        if (p.url) window.location.href = p.url;
                      } catch (e) { oops(e); }
                    }}>
                    Pay now
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {mine && (
        <div className="border border-border bg-white p-4" data-testid="catch-land-box">
          <p className="overline text-slate-500">Boats and fishmongers — land today's catch</p>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <select data-testid="catch-species" value={lot.species} aria-label="Species"
              onChange={(e) => setLot({ ...lot, species: e.target.value })}
              className="tap-safe border border-border bg-white px-3 py-2 text-sm">
              {(data.species || []).map((s) => <option key={s} value={s}>{String(s)}</option>)}
            </select>
            <Input data-testid="catch-local-name" placeholder="What you call it locally — 'bombil'"
              value={lot.local_name} onChange={(e) => setLot({ ...lot, local_name: e.target.value })} />
            <select data-testid="catch-sell-by" value={lot.sell_by} aria-label="Sold by"
              onChange={(e) => setLot({ ...lot, sell_by: e.target.value })}
              className="tap-safe border border-border bg-white px-3 py-2 text-sm">
              {(data.sell_by || []).map((s) => <option key={s} value={s}>{String(s)}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              Your price
              <Input data-testid="catch-price" type="number" min={0} step="0.01" value={lot.price}
                onChange={(e) => setLot({ ...lot, price: e.target.value })} className="w-24" />
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              How much landed
              <Input data-testid="catch-quantity" type="number" min={0} value={lot.quantity}
                onChange={(e) => setLot({ ...lot, quantity: e.target.value })} className="w-24" />
            </label>
            <select data-testid="catch-state" value={lot.state} aria-label="State"
              onChange={(e) => setLot({ ...lot, state: e.target.value })}
              className="tap-safe border border-border bg-white px-3 py-2 text-sm">
              {(data.catch_state || []).map((s) => <option key={s} value={s}>{String(s)}</option>)}
            </select>
            <Input data-testid="catch-boat" placeholder="Boat name"
              value={lot.boat} onChange={(e) => setLot({ ...lot, boat: e.target.value })} />
            <Input data-testid="catch-harbour" placeholder="Harbour or jetty"
              value={lot.harbour} onChange={(e) => setLot({ ...lot, harbour: e.target.value })} />
            <label className="flex items-center gap-2 text-sm text-slate-600">
              Out within (hours of landing)
              <Input data-testid="catch-coldchain" type="number" min={0} max={72} value={lot.cold_chain_hours}
                onChange={(e) => setLot({ ...lot, cold_chain_hours: e.target.value })} className="w-20" />
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" data-testid="catch-icepack" checked={lot.ice_pack}
                onChange={(e) => setLot({ ...lot, ice_pack: e.target.checked })} />
              Ice pack with every delivery
            </label>
            <Input data-testid="catch-note" placeholder="Note — 'netted at first light'"
              value={lot.note} onChange={(e) => setLot({ ...lot, note: e.target.value })} />
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            {(data.preps || []).map((p) => (
              <label key={p} className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" data-testid={`catch-prep-offer-${p.replace(/[^a-z]/gi, "-")}`}
                  checked={lot.preps.includes(p)} onChange={() => togglePrep(p)} />
                {p}
              </label>
            ))}
          </div>
          <div className="mt-3">
            <ImageCaptionField testid="catch-image" label="Photo of the catch — goes on the public board"
              value={lot.image_id} caption={lot.image_caption}
              onChange={({ image_id, image_caption }) => setLot({ ...lot, image_id: image_id || "", image_caption })} />
          </div>
          <Button className="mt-2" size="sm" data-testid="catch-save" disabled={busy}
            onClick={() => act(async () => {
              await api.post("/farm/catch", {
                stall_id: mine.id, species: lot.species, local_name: lot.local_name,
                sell_by: lot.sell_by, price: Number(lot.price) || 0,
                quantity: Number(lot.quantity) || 0, state: lot.state,
                boat: lot.boat, harbour: lot.harbour, preps: lot.preps,
                ice_pack: lot.ice_pack,
                cold_chain_hours: Number(lot.cold_chain_hours) || 0,
                note: lot.note, image_id: lot.image_id || "",
              });
              setLot({ ...lot, local_name: "", note: "", image_id: "", image_caption: "" });
            }, "Landed on the board")}>
            Put the catch up
          </Button>
        </div>
      )}
    </div>
  );
};
